/**
 * StyleProfileService
 * 
 * Analyse le dressing de l'utilisateur (via ai_metadata) pour
 * en extraire un profil stylistique : formality moyenne, couleurs
 * dominantes, styles détectés, budget moyen...
 * 
 * Tout est calculé côté client depuis les ai_metadata stockées.
 */

// Labels de formality
const FORMALITY_LABELS = [
  { min: 0,   max: 1.5, label: 'Sportwear décontracté',    emoji: '🏃' },
  { min: 1.5, max: 2.5, label: 'Casual relax',              emoji: '👕' },
  { min: 2.5, max: 3.5, label: 'Casual chic',               emoji: '🌆' },
  { min: 3.5, max: 4.2, label: 'Chic moderne',              emoji: '✨' },
  { min: 4.2, max: 5.1, label: 'Ultra chic / soirée',       emoji: '👑' },
];

// Classification couleur → famille
const COLOR_FAMILIES = {
  noir:    { family: 'Monochrome', hex: '#111',    emoji: '⚫' },
  blanc:   { family: 'Monochrome', hex: '#FFF',    emoji: '⚪' },
  gris:    { family: 'Monochrome', hex: '#888',    emoji: '🔘' },
  beige:   { family: 'Neutres',    hex: '#D4C4A0', emoji: '🟤' },
  marron:  { family: 'Neutres',    hex: '#8B4513', emoji: '🟫' },
  bleu:    { family: 'Froids',     hex: '#2E5BBA', emoji: '🔵' },
  marine:  { family: 'Froids',     hex: '#0A2847', emoji: '🔵' },
  vert:    { family: 'Naturels',   hex: '#3D7F4A', emoji: '🟢' },
  kaki:    { family: 'Naturels',   hex: '#707244', emoji: '🟢' },
  rouge:   { family: 'Chauds',     hex: '#D32F2F', emoji: '🔴' },
  rose:    { family: 'Chauds',     hex: '#E91E63', emoji: '🌸' },
  orange:  { family: 'Chauds',     hex: '#F57C00', emoji: '🟠' },
  jaune:   { family: 'Chauds',     hex: '#FBC02D', emoji: '🟡' },
  violet:  { family: 'Chics',      hex: '#7B1FA2', emoji: '🟣' },
  bordeaux:{ family: 'Chics',      hex: '#5C1E27', emoji: '🍷' },
  doré:    { family: 'Précieux',   hex: '#B8860B', emoji: '✨' },
  argenté: { family: 'Précieux',   hex: '#9E9E9E', emoji: '💎' },
};

function normalizeColor(c) {
  if (!c) return null;
  const low = c.toLowerCase().trim();
  // Match direct
  if (COLOR_FAMILIES[low]) return low;
  // Match par mot
  for (const key of Object.keys(COLOR_FAMILIES)) {
    if (low.includes(key)) return key;
  }
  return null;
}

function inferStylesFromItems(items) {
  // Déduit les styles dominants (min 2 articles pour compter)
  const styleCounts = {};
  for (const item of items) {
    const styles = item.ai_metadata?.styles;
    if (Array.isArray(styles)) {
      for (const s of styles) {
        const key = String(s).toLowerCase().trim();
        if (key.length > 2) styleCounts[key] = (styleCounts[key] || 0) + 1;
      }
    }
  }
  return Object.entries(styleCounts)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([style]) => style.charAt(0).toUpperCase() + style.slice(1));
}

/**
 * Analyse un tableau d'articles du dressing et retourne un profil stylistique
 */
export function computeStyleProfile(wardrobeItems) {
  if (!Array.isArray(wardrobeItems) || wardrobeItems.length === 0) {
    return null;
  }

  const analyzed = wardrobeItems.filter(i => i?.ai_metadata?.analyzedAt);
  if (analyzed.length === 0) {
    return {
      empty: true,
      analyzedCount: 0,
      totalCount: wardrobeItems.length,
    };
  }

  // 1. Formality moyenne
  // v1.12.119 — clamp [0, 5] sur chaque valeur (Claude peut occasionnellement
  // retourner 5.5 ou 6 si la consigne n'est pas suivie strictement) puis sur
  // la moyenne finale, pour ne JAMAIS afficher "5.1 / 5" sur le profil.
  const formalities = analyzed
    .map(i => i.ai_metadata?.formality)
    .filter(f => typeof f === 'number' && !isNaN(f))
    .map(f => Math.max(0, Math.min(5, f)));
  const avgFormalityRaw = formalities.length > 0
    ? formalities.reduce((a, b) => a + b, 0) / formalities.length
    : null;
  const avgFormality = avgFormalityRaw != null
    ? Math.max(0, Math.min(5, avgFormalityRaw))
    : null;
  const formalityProfile = avgFormality != null
    ? FORMALITY_LABELS.find(f => avgFormality >= f.min && avgFormality < f.max) || FORMALITY_LABELS[2]
    : null;

  // 2. Couleurs dominantes
  const colorCounts = {};
  for (const item of analyzed) {
    const color = normalizeColor(item.ai_metadata?.primaryColor || item.color);
    if (color) colorCounts[color] = (colorCounts[color] || 0) + 1;
  }
  const topColors = Object.entries(colorCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([color, count]) => ({
      name: color.charAt(0).toUpperCase() + color.slice(1),
      count,
      ...COLOR_FAMILIES[color],
      percent: Math.round((count / analyzed.length) * 100),
    }));

  // 3. Familles de couleurs
  const familyCounts = {};
  for (const [color, count] of Object.entries(colorCounts)) {
    const fam = COLOR_FAMILIES[color]?.family || 'Autres';
    familyCounts[fam] = (familyCounts[fam] || 0) + count;
  }
  const dominantFamily = Object.entries(familyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Variés';

  // 4. Styles détectés
  const styles = inferStylesFromItems(analyzed);

  // 5. Catégories les plus représentées
  const catCounts = {};
  for (const item of wardrobeItems) {
    const cat = item.category || 'Autre';
    catCounts[cat] = (catCounts[cat] || 0) + 1;
  }
  const topCategory = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0] || ['Autre', 0];

  // 6. Prix moyen (si dispo)
  const prices = wardrobeItems
    .map(i => parseFloat(i.price))
    .filter(p => !isNaN(p) && p > 0);
  const avgPrice = prices.length > 0
    ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
    : null;

  // 7. Score de diversité (0-100)
  const uniqueCats = Object.keys(catCounts).length;
  const uniqueColors = Object.keys(colorCounts).length;
  const diversityScore = Math.min(100, uniqueCats * 10 + uniqueColors * 8);

  // 8. Profil type — tagline
  let tagline = '';
  if (avgFormality != null && formalityProfile) {
    if (dominantFamily === 'Monochrome') tagline = `${formalityProfile.label}, palette ${dominantFamily.toLowerCase()}`;
    else if (dominantFamily === 'Neutres') tagline = `${formalityProfile.label}, tons ${dominantFamily.toLowerCase()}`;
    else tagline = `${formalityProfile.label}, couleurs ${dominantFamily.toLowerCase()}`;
  }

  return {
    empty: false,
    analyzedCount: analyzed.length,
    totalCount: wardrobeItems.length,
    formality:       avgFormality != null ? Math.round(avgFormality * 10) / 10 : null,
    formalityLabel:  formalityProfile?.label || null,
    formalityEmoji:  formalityProfile?.emoji || null,
    topColors,
    dominantFamily,
    styles,
    topCategory:     topCategory[0],
    topCategoryCount: topCategory[1],
    avgPrice,
    diversityScore,
    tagline,
  };
}
